import { useEffect, useMemo, useState } from "react";
import { getStationsByCustomerId } from "../services/clearing/customers";
import { getStationMonthlyReport } from "../services/clearing/analytics";
import { getCdrs } from "../services/clearing/transactions";
import {
  AnalyticsPeriod,
  CDRTransaction,
  MonthlyAggregate,
  StationAggregate,
  ProviderAggregate,
  UserAggregate,
  ReportData,
  DetailData,
  StationMonthlyReport,
} from "../types/analytics";

const CONCURRENCY_LIMIT = 10;

// --- Helpers ---

function getMonthsRange(
  period: AnalyticsPeriod
): { year: number; month: number }[] {
  const months: { year: number; month: number }[] = [];
  const now = new Date();

  if (typeof period === "number" && period >= 2000) {
    const year = period;
    const lastMonth = year === now.getFullYear() ? now.getMonth() + 1 : 12;
    for (let m = 1; m <= lastMonth; m++) {
      months.push({ year, month: m });
    }
    return months;
  }

  for (let i = (period as number) - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
  }

  return months;
}

function formatMonthLabel(year: number, month: number): string {
  const d = new Date(year, month - 1, 1);
  return d.toLocaleString("en", { month: "short", year: "2-digit" });
}

function formatUserId(userId: string): string {
  if (!userId) return "\u2014";
  if (userId === "AdHoc") return "AdHoc";
  if (userId.startsWith("DE-COC-")) return userId.slice(7);
  if (userId.length > 8) return `\u2026${userId.slice(-6)}`;
  return userId;
}

async function runWithConcurrency<T>(
  tasks: (() => Promise<T>)[],
  limit: number
): Promise<T[]> {
  const results: T[] = [];
  let index = 0;

  async function runNext(): Promise<void> {
    while (index < tasks.length) {
      const currentIndex = index++;
      results[currentIndex] = await tasks[currentIndex]();
    }
  }

  const workers = Array.from(
    { length: Math.min(limit, tasks.length) },
    () => runNext()
  );
  await Promise.all(workers);
  return results;
}

// --- Phase 1: Aggregate report API data ---

type ReportResult = {
  stationId: string;
  year: number;
  month: number;
  report: StationMonthlyReport | null;
};

function aggregateReportData(
  results: ReportResult[],
  months: { year: number; month: number }[]
): ReportData {
  const monthly: MonthlyAggregate[] = months.map(({ year, month }) => {
    const monthResults = results.filter(
      (r) => r.year === year && r.month === month && r.report
    );
    const revenue = monthResults.reduce(
      (sum, r) =>
        sum + (Number(r.report?.currentMonthMetrics?.totalRevenue) || 0),
      0
    );
    const sessions = monthResults.reduce(
      (sum, r) =>
        sum + (Number(r.report?.currentMonthMetrics?.sessionCount) || 0),
      0
    );
    const energy = monthResults.reduce(
      (sum, r) =>
        sum + (Number(r.report?.currentMonthMetrics?.totalEnergy) || 0),
      0
    );

    return {
      year,
      month,
      label: formatMonthLabel(year, month),
      revenue,
      sessions,
      energy,
      paidSessions: 0,
      freeSessions: 0,
      paidEnergy: 0,
      freeEnergy: 0,
    };
  });

  const stationMap = new Map<string, StationAggregate>();
  for (const r of results) {
    if (!r.report) continue;
    const metrics = r.report.currentMonthMetrics;
    const existing = stationMap.get(r.stationId) || {
      stationId: r.stationId,
      revenue: 0,
      sessions: 0,
      energy: 0,
    };
    existing.revenue += Number(metrics?.totalRevenue) || 0;
    existing.sessions += Number(metrics?.sessionCount) || 0;
    existing.energy += Number(metrics?.totalEnergy) || 0;
    stationMap.set(r.stationId, existing);
  }

  const totalRevenue = monthly.reduce((s, m) => s + m.revenue, 0);
  const totalSessions = monthly.reduce((s, m) => s + m.sessions, 0);
  const totalEnergy = monthly.reduce((s, m) => s + m.energy, 0);

  return {
    monthly,
    stations: Array.from(stationMap.values()),
    totalRevenue,
    totalSessions,
    totalEnergy,
    avgPerSession: totalSessions > 0 ? totalRevenue / totalSessions : 0,
  };
}

// --- Phase 2: Aggregate raw CDR data ---

type CdrResult = {
  stationId: string;
  year: number;
  month: number;
  transactions: CDRTransaction[];
};

function aggregateCdrData(
  results: CdrResult[],
  months: { year: number; month: number }[]
): DetailData {
  const allTx = results.flatMap((r) => r.transactions);
  const paidSessions = allTx.filter((tx) => !tx.freeCharge).length;
  const freeSessions = allTx.filter((tx) => !!tx.freeCharge).length;
  const paidEnergy = allTx
    .filter((tx) => !tx.freeCharge)
    .reduce((sum, tx) => sum + (Number(tx.totalEnergy) || 0), 0);
  const freeEnergy = allTx
    .filter((tx) => !!tx.freeCharge)
    .reduce((sum, tx) => sum + (Number(tx.totalEnergy) || 0), 0);

  // Provider breakdown
  const providerMap = new Map<
    string,
    {
      sessions: number;
      revenue: number;
      energy: number;
      userIds: Set<string>;
    }
  >();
  for (const tx of allTx) {
    const provider = String(tx.emobilityProviderName || "Unknown");
    const txRevenue = tx.freeCharge ? 0 : Number(tx.totalCost) || 0;
    const txEnergy = Number(tx.totalEnergy) || 0;
    const uid = tx.userId || "Unknown";
    const existing = providerMap.get(provider);
    if (existing) {
      existing.sessions += 1;
      existing.revenue += txRevenue;
      existing.energy += txEnergy;
      existing.userIds.add(uid);
    } else {
      providerMap.set(provider, {
        sessions: 1,
        revenue: txRevenue,
        energy: txEnergy,
        userIds: new Set([uid]),
      });
    }
  }
  const providers: ProviderAggregate[] = Array.from(providerMap.entries())
    .map(([provider, data]) => ({
      provider,
      sessions: data.sessions,
      revenue: data.revenue,
      energy: data.energy,
      uniqueUsers: data.userIds.size,
    }))
    .sort((a, b) => b.sessions - a.sessions);

  // Per-user aggregation
  const userMap = new Map<
    string,
    {
      provider: string;
      sessions: number;
      energy: number;
      revenue: number;
      lastSession: string;
      transactions: CDRTransaction[];
    }
  >();
  for (const tx of allTx) {
    const uid = tx.userId || "Unknown";
    const txRevenue = tx.freeCharge ? 0 : Number(tx.totalCost) || 0;
    const txEnergy = Number(tx.totalEnergy) || 0;
    const txDate = tx.startDateTime || "";
    const existing = userMap.get(uid);
    if (existing) {
      existing.sessions += 1;
      existing.energy += txEnergy;
      existing.revenue += txRevenue;
      existing.transactions.push(tx);
      if (txDate > existing.lastSession) existing.lastSession = txDate;
    } else {
      userMap.set(uid, {
        provider: String(tx.emobilityProviderName || "Unknown"),
        sessions: 1,
        energy: txEnergy,
        revenue: txRevenue,
        lastSession: txDate,
        transactions: [tx],
      });
    }
  }

  const allUsers: UserAggregate[] = Array.from(userMap.entries())
    .map(([userId, data]) => ({
      userId,
      displayId: formatUserId(userId),
      provider: data.provider,
      sessions: data.sessions,
      energy: data.energy,
      revenue: data.revenue,
      lastSession: data.lastSession,
      isReturning: data.sessions > 1,
      transactions: data.transactions,
    }))
    .sort((a, b) => b.sessions - a.sessions);

  const returningCount = allUsers.filter((u) => u.isReturning).length;
  const topUsers = allUsers.slice(0, 50);

  return {
    providers,
    topUsers,
    uniqueUsers: userMap.size,
    returningUsers: returningCount,
    paidSessions,
    freeSessions,
    paidEnergy,
    freeEnergy,
  };
}

// Update monthly aggregates with paid/free splits from CDR data
function enrichMonthlyWithCdrData(
  monthly: MonthlyAggregate[],
  cdrResults: CdrResult[]
): MonthlyAggregate[] {
  return monthly.map((m) => {
    const monthTx = cdrResults
      .filter((r) => r.year === m.year && r.month === m.month)
      .flatMap((r) => r.transactions);
    const paidTx = monthTx.filter((tx) => !tx.freeCharge);
    const freeTx = monthTx.filter((tx) => !!tx.freeCharge);
    return {
      ...m,
      paidSessions: paidTx.length,
      freeSessions: freeTx.length,
      paidEnergy: paidTx.reduce(
        (sum, tx) => sum + (Number(tx.totalEnergy) || 0),
        0
      ),
      freeEnergy: freeTx.reduce(
        (sum, tx) => sum + (Number(tx.totalEnergy) || 0),
        0
      ),
    };
  });
}

// --- Hook ---

export interface UseCustomerAnalyticsResult {
  stationIds: string[];
  reportData: ReportData | null;
  detailData: DetailData | null;
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
}

export const useCustomerAnalytics = (
  customerId: string | undefined,
  period: AnalyticsPeriod,
  selectedStationIds?: string[] | null
): UseCustomerAnalyticsResult => {
  const [stationIds, setStationIds] = useState<string[]>([]);
  const [allReportResults, setAllReportResults] = useState<ReportResult[]>([]);
  const [allCdrResults, setAllCdrResults] = useState<CdrResult[]>([]);
  const [months, setMonths] = useState<{ year: number; month: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const fetchAll = async () => {
      if (!customerId) {
        setLoading(false);
        setDetailLoading(false);
        return;
      }

      setLoading(true);
      setDetailLoading(true);
      setError(null);
      setAllReportResults([]);
      setAllCdrResults([]);

      // Step 1: Fetch station IDs
      let ids: string[];
      try {
        ids = await getStationsByCustomerId({ customerId });
        if (!mounted) return;
        if (!ids || ids.length === 0) {
          setStationIds([]);
          setLoading(false);
          setDetailLoading(false);
          return;
        }
        setStationIds(ids);
      } catch (err) {
        console.error("Failed to fetch stations for customer:", err);
        if (!mounted) return;
        setError("Failed to load customer stations.");
        setLoading(false);
        setDetailLoading(false);
        return;
      }

      const monthsRange = getMonthsRange(period);
      if (!mounted) return;
      setMonths(monthsRange);

      // Phase 1: Fetch report API data
      const reportTasks = ids.flatMap((stationId) =>
        monthsRange.map(
          ({ year, month }) =>
            async (): Promise<ReportResult> => {
              const report = await getStationMonthlyReport(
                stationId,
                year,
                month
              );
              return { stationId, year, month, report };
            }
        )
      );

      try {
        const reportResults = await runWithConcurrency(
          reportTasks,
          CONCURRENCY_LIMIT
        );
        if (!mounted) return;
        setAllReportResults(reportResults);
      } catch (err) {
        console.error("Failed to fetch report data:", err);
      } finally {
        if (mounted) setLoading(false);
      }

      // Phase 2: Fetch raw CDR data
      const cdrTasks = ids.flatMap((stationId) =>
        monthsRange.map(
          ({ year, month }) =>
            async (): Promise<CdrResult> => {
              try {
                const transactions = await getCdrs(stationId, year, month);
                const validTx = (transactions || []).filter(
                  Boolean
                ) as CDRTransaction[];
                return { stationId, year, month, transactions: validTx };
              } catch (err) {
                console.error(
                  `Failed to fetch CDRs for ${stationId} (${year}-${month}):`,
                  err
                );
                return { stationId, year, month, transactions: [] };
              }
            }
        )
      );

      try {
        const cdrResults = await runWithConcurrency(
          cdrTasks,
          CONCURRENCY_LIMIT
        );
        if (!mounted) return;
        setAllCdrResults(cdrResults);
      } catch (err) {
        console.error("Failed to fetch CDR data:", err);
      } finally {
        if (mounted) setDetailLoading(false);
      }
    };

    fetchAll();
    return () => {
      mounted = false;
    };
  }, [customerId, period]);

  // Apply station filter and aggregate
  const reportData = useMemo(() => {
    if (loading || allReportResults.length === 0) return null;
    const filtered =
      selectedStationIds && selectedStationIds.length > 0
        ? allReportResults.filter((r) =>
            selectedStationIds.includes(r.stationId)
          )
        : allReportResults;
    return aggregateReportData(filtered, months);
  }, [allReportResults, months, selectedStationIds, loading]);

  const detailData = useMemo(() => {
    if (detailLoading || allCdrResults.length === 0) return null;
    const filtered =
      selectedStationIds && selectedStationIds.length > 0
        ? allCdrResults.filter((r) =>
            selectedStationIds.includes(r.stationId)
          )
        : allCdrResults;
    return aggregateCdrData(filtered, months);
  }, [allCdrResults, months, selectedStationIds, detailLoading]);

  // Enrich monthly data with paid/free splits when CDR data is available
  const enrichedReportData = useMemo(() => {
    if (!reportData) return null;
    if (detailLoading || allCdrResults.length === 0) return reportData;
    const filteredCdr =
      selectedStationIds && selectedStationIds.length > 0
        ? allCdrResults.filter((r) =>
            selectedStationIds.includes(r.stationId)
          )
        : allCdrResults;
    return {
      ...reportData,
      monthly: enrichMonthlyWithCdrData(reportData.monthly, filteredCdr),
    };
  }, [reportData, allCdrResults, selectedStationIds, detailLoading]);

  return {
    stationIds,
    reportData: enrichedReportData,
    detailData,
    loading,
    detailLoading,
    error,
  };
};
